import { useState, useEffect } from "react";
import { Plus, Search, Edit2, Trash2, UserPlus, CheckCircle, Clock } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

export default function Interns() {
    const [interns, setInterns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    
    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [form, setForm] = useState({
        fullName: "",
        email: "",
        phone: "",
        role: "Intern",
        department: "",
        startDate: "",
        endDate: "",
        status: "active",
        mentor: ""
    });

    useEffect(() => {
        fetchInterns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, statusFilter]);

    const fetchInterns = async () => {
        setLoading(true);
        try {
            const params = {};
            if (search) params.search = search;
            if (statusFilter) params.status = statusFilter;
            
            const r = await api.get("/interns", { params });
            setInterns(r.data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const handleOpenAdd = () => {
        setIsEdit(false);
        setForm({
            fullName: "",
            email: "",
            phone: "",
            role: "Intern",
            department: "",
            startDate: "",
            endDate: "",
            status: "active",
            mentor: ""
        });
        setShowModal(true);
    };

    const handleOpenEdit = (intern) => {
        setIsEdit(true);
        setSelectedId(intern.id);
        setForm({
            fullName: intern.fullName || "",
            email: intern.email || "",
            phone: intern.phone || "",
            role: intern.role || "Intern",
            department: intern.department || "",
            startDate: intern.startDate || "",
            endDate: intern.endDate || "",
            status: intern.status || "active",
            mentor: intern.mentor || ""
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEdit) {
                await api.put(`/interns/${selectedId}`, form);
                toast.success("Intern updated successfully");
            } else {
                await api.post("/interns", form);
                toast.success("Intern added successfully");
            }
            setShowModal(false);
            fetchInterns();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const handleDelete = async (id) => {
        if (!confirm("Are you sure you want to delete this intern?")) return;
        try {
            await api.delete(`/interns/${id}`);
            toast.success("Intern deleted successfully");
            fetchInterns();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="space-y-6 p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-heading font-bold text-slate-800 dark:text-slate-100">Intern Management</h1>
                    <p className="text-sm text-muted-foreground">Manage details of active, completed, and pending interns.</p>
                </div>
                <button
                    onClick={handleOpenAdd}
                    className="flex items-center gap-2 bg-primary hover:bg-primary-hover text-white px-4 py-2 rounded-xl transition duration-300 shadow-md font-semibold text-sm"
                >
                    <UserPlus className="w-4 h-4" />
                    <span>Add Intern</span>
                </button>
            </div>

            {/* Filters */}
            <div className="glass p-4 rounded-2xl flex flex-col md:flex-row gap-4 items-center justify-between shadow-sm">
                <div className="relative w-full md:w-80">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Search by name, email, or role..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                    />
                </div>
                
                <div className="flex gap-3 w-full md:w-auto">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                    >
                        <option value="">All Statuses</option>
                        <option value="active">Active</option>
                        <option value="completed">Completed</option>
                        <option value="terminated">Terminated</option>
                    </select>
                </div>
            </div>

            {/* Grid/List */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
            ) : interns.length === 0 ? (
                <div className="glass py-16 text-center rounded-2xl border border-slate-200 dark:border-white/10">
                    <p className="text-muted-foreground text-sm">No interns found. Click "Add Intern" to create one.</p>
                </div>
            ) : (
                <div className="glass overflow-hidden rounded-2xl border border-slate-200 dark:border-white/10 shadow-lg">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 dark:bg-slate-800/50 text-xs font-semibold text-muted-foreground border-b border-slate-200 dark:border-white/10">
                                    <th className="px-6 py-4">Name</th>
                                    <th className="px-6 py-4">Email / Phone</th>
                                    <th className="px-6 py-4">Role & Dept</th>
                                    <th className="px-6 py-4">Duration</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4">Mentor</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 dark:divide-white/10 text-sm text-foreground">
                                {interns.map((i) => (
                                    <tr key={i.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition">
                                        <td className="px-6 py-4 font-semibold text-slate-800 dark:text-slate-100">{i.fullName}</td>
                                        <td className="px-6 py-4">
                                            <div className="text-slate-700 dark:text-slate-300">{i.email}</div>
                                            <div className="text-xs text-muted-foreground mt-0.5">{i.phone || "N/A"}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-slate-700 dark:text-slate-300 font-medium">{i.role}</div>
                                            <div className="text-xs text-muted-foreground mt-0.5">{i.department || "N/A"}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-xs">
                                            <div>{i.startDate || "N/A"}</div>
                                            <div className="text-muted-foreground mt-0.5">to {i.endDate || "N/A"}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium capitalize
                                                ${i.status === "active" ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400" :
                                                  i.status === "completed" ? "bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400" :
                                                  "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400"}`}
                                            >
                                                {i.status === "active" && <Clock className="w-3.5 h-3.5" />}
                                                {i.status === "completed" && <CheckCircle className="w-3.5 h-3.5" />}
                                                {i.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-slate-600 dark:text-slate-400">{i.mentor || "None"}</td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => handleOpenEdit(i)}
                                                    className="p-1.5 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg text-slate-500 hover:text-primary transition"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(i.id)}
                                                    className="p-1.5 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg text-slate-500 hover:text-rose-600 transition"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modal Dialog */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-lg overflow-hidden border border-slate-200 dark:border-white/10 shadow-2xl animate-in fade-in zoom-in duration-200">
                        <div className="px-6 py-4 border-b border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-800/50 flex justify-between items-center">
                            <h2 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100">
                                {isEdit ? "Edit Intern Details" : "Add New Intern"}
                            </h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-muted-foreground hover:text-foreground text-sm font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Full Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={form.fullName}
                                        onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Email</label>
                                    <input
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Phone</label>
                                    <input
                                        type="text"
                                        value={form.phone}
                                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Department</label>
                                    <input
                                        type="text"
                                        value={form.department}
                                        onChange={(e) => setForm({ ...form, department: e.target.value })}
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Role</label>
                                    <input
                                        type="text"
                                        value={form.role}
                                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Start Date</label>
                                    <input
                                        type="date"
                                        required
                                        value={form.startDate}
                                        onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">End Date</label>
                                    <input
                                        type="date"
                                        required
                                        value={form.endDate}
                                        onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Status</label>
                                    <select
                                        value={form.status}
                                        onChange={(e) => setForm({ ...form, status: e.target.value })}
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                    >
                                        <option value="active">Active</option>
                                        <option value="completed">Completed</option>
                                        <option value="terminated">Terminated</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Mentor</label>
                                    <input
                                        type="text"
                                        value={form.mentor}
                                        onChange={(e) => setForm({ ...form, mentor: e.target.value })}
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/10">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 border border-slate-200 dark:border-white/10 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-xl text-sm font-semibold shadow-md transition"
                                >
                                    {isEdit ? "Save Changes" : "Create Intern"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
